import { BoundingArea } from "@common/components/_models";
import { useAppStore } from "@common/stores/appStore";
import { debounce, isEqual } from "lodash-es";
import { computed, onBeforeUnmount, onMounted, ref, type Ref } from "vue";

export interface BufferSize {
  top?: number;
  bottom?: number;
  left?: number;
  right?: number;
}

type ElType = HTMLElement | undefined | null;

export function useDocOverflowDetection(target: Ref<ElType>, rootEl: Ref<ElType>, bufferOverrids?: BufferSize) {
  const appStore = useAppStore();

  const targetBoundingArea = ref<BoundingArea>();
  const rootBoundingArea = ref<BoundingArea>();

  let resizeObserver: ResizeObserver | null = null;

  const docOverflow = computed(() => calcDocOverflow(targetBoundingArea.value, rootBoundingArea.value, appStore.documentBoundingArea, bufferOverrids));

  // Lifecycle
  onMounted(() => {
    if (target.value) {
      resizeObserver = new ResizeObserver(
        debounce((entries: ResizeObserverEntry[]) => {
          // Use contentRect from the entry to avoid re-querying the DOM
          const entry = entries[0];
          if (!entry) return;

          const newTargetRect = target.value?.getBoundingClientRect().toJSON();
          const newRootRect = rootEl.value?.getBoundingClientRect().toJSON();

          // Only update if values actually changed to prevent infinite loops
          if (!isEqual(newTargetRect, targetBoundingArea.value)) {
            targetBoundingArea.value = newTargetRect;
          }
          if (!isEqual(newRootRect, rootBoundingArea.value)) {
            rootBoundingArea.value = newRootRect;
          }
        }, 200)
      );
      resizeObserver.observe(target.value);
    }
  });

  onBeforeUnmount(() => {
    if (resizeObserver) {
      resizeObserver.disconnect();
      resizeObserver = null;
    }
  });

  return {
    docOverflow,
  };
}

export function calcDocOverflow(
  boundingArea: BoundingArea | undefined | null,
  rootArea: BoundingArea | undefined | null,
  docBoundingArea: BoundingArea,
  buffers?: BufferSize
) {
  const docBottom = docBoundingArea.top + docBoundingArea.height;
  const docRight = docBoundingArea.left + docBoundingArea.width;
  const bufferSize = 8;

  const getBufferSize = (direction: keyof BufferSize) => {
    if (!buffers) return bufferSize;

    const value = buffers[direction];
    if (!value || (typeof value === "number" && Number.isNaN(value))) return bufferSize;
    return value;
  };

  // Available space is calculated from the root element (activator), not the target (menu)
  const availableSpace = {
    top: rootArea ? rootArea.top - docBoundingArea.top - getBufferSize("top") : 0,
    bottom: rootArea ? docBottom - rootArea.bottom - getBufferSize("bottom") : 0,
    left: rootArea ? rootArea.left - docBoundingArea.left - getBufferSize("left") : 0,
    right: rootArea ? docRight - rootArea.right - getBufferSize("right") : 0,
  };

  // Overflow is determined by comparing the target's size against available space
  const targetHeight = boundingArea?.height ?? 0;
  const targetWidth = boundingArea?.width ?? 0;

  return {
    targetHeight,
    targetWidth,
    top: targetHeight > availableSpace.top,
    bottom: targetHeight > availableSpace.bottom,
    left: targetWidth > availableSpace.left,
    right: targetWidth > availableSpace.right,
    availableSpace,
  };
}
